/**
 * Une SKILLCARD en « mode leçon » : le contenu du recto (sections structurées,
 * schémas compris) rendu en pleine largeur, lisible sur mobile — sans le cadre
 * de la carte à jouer. Utilisée par le bouton « Cours » du player V2.
 */

import { Lightbulb, AlertTriangle, Target } from 'lucide-react';
import { MarkdownMath, MathText, VisualBlockRenderer } from '@jeraisonne/ui';
import type { ExerciseVisualBlock } from '@jeraisonne/ai/visual-blocks';
import { HORA_METHOD, type CardSection } from '@jeraisonne/db/cards';

/** Temps HORA d'une section (repère visuel du recto). */
export function horaStepOf(section: CardSection): 'H' | 'O' | 'R' | 'A' | null {
  switch (section.kind) {
    case 'when_to_use':
      return 'H';
    case 'formula':
    case 'markdown':
    case 'table':
      return 'O';
    case 'steps':
      return 'R';
    case 'example':
      return 'A';
    default:
      return null;
  }
}

const HORA_BADGE: Record<'H' | 'O' | 'R' | 'A', string> = {
  H: 'bg-[#3b5bdb]',
  O: 'bg-[#2f9e44]',
  R: 'bg-[#f08c00]',
  A: 'bg-[#e03131]',
};

function HoraBadge({ step }: { step: 'H' | 'O' | 'R' | 'A' }) {
  const meta = HORA_METHOD.find((s) => s.key === step)!;
  return (
    <span
      className={`inline-flex h-6 items-center gap-1 rounded-full px-2 text-[11px] font-bold text-white ${HORA_BADGE[step]}`}
      title={meta.title}
    >
      {step} · {meta.title}
    </span>
  );
}

function SectionBody({ section }: { section: CardSection }) {
  switch (section.kind) {
    case 'steps':
      return (
        <ol className="text-ink-800 list-decimal space-y-1 pl-5 text-sm">
          {section.items.map((item, i) => (
            <li key={i}>
              <MarkdownMath>{item}</MarkdownMath>
            </li>
          ))}
        </ol>
      );
    case 'formula':
      return (
        <div className="space-y-2">
          <MarkdownMath>{`$$${section.latex}$$`}</MarkdownMath>
          {section.variables?.length ? (
            <ul className="text-ink-700 space-y-0.5 text-sm">
              {section.variables.map((v, i) => (
                <li key={i} className="flex gap-2">
                  <MarkdownMath>{`$${v.symbol}$`}</MarkdownMath>
                  <span>:</span>
                  <MarkdownMath>{v.meaning}</MarkdownMath>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      );
    case 'table':
      return (
        <div className="overflow-x-auto">
          <table className="text-ink-800 w-full border-collapse text-sm">
            <thead>
              <tr>
                {section.headers.map((h, i) => (
                  <th key={i} className="border-ink-200 border px-2 py-1 text-left font-semibold">
                    <MarkdownMath>{h}</MarkdownMath>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {section.rows.map((row, r) => (
                <tr key={r}>
                  {row.map((cell, c) => (
                    <td key={c} className="border-ink-200 border px-2 py-1">
                      <MarkdownMath>{cell}</MarkdownMath>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case 'markdown':
      return <MarkdownMath className="text-ink-800">{section.md}</MarkdownMath>;
    case 'example':
      return (
        <div className="bg-ink-50 space-y-1 rounded-xl p-3 text-sm">
          <MarkdownMath className="text-ink-900 font-medium">{section.statement}</MarkdownMath>
          {section.steps.length > 0 && (
            <ol className="text-ink-700 list-decimal space-y-0.5 pl-5">
              {section.steps.map((s, i) => (
                <li key={i}>
                  <MarkdownMath>{s}</MarkdownMath>
                </li>
              ))}
            </ol>
          )}
          {section.result ? (
            <p className="text-ink-900 font-semibold">
              → <MathText>{section.result}</MathText>
            </p>
          ) : null}
        </div>
      );
    case 'when_to_use':
      return (
        <div className="flex gap-2 text-sm">
          <Target className="text-primary-500 mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <MarkdownMath className="text-ink-800">{section.md}</MarkdownMath>
        </div>
      );
    case 'pitfall':
      return (
        <div className="flex gap-2 rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden />
          <MarkdownMath className="text-ink-800">{section.md}</MarkdownMath>
        </div>
      );
    case 'visual':
      return (
        <figure className="space-y-1">
          <div className="overflow-x-auto">
            <VisualBlockRenderer block={section.block as unknown as ExerciseVisualBlock} />
          </div>
          {section.caption ? (
            <figcaption className="text-ink-500 text-xs">{section.caption}</figcaption>
          ) : null}
        </figure>
      );
    default:
      return null;
  }
}

function sectionTitle(section: CardSection): string | null {
  if ('title' in section && section.title) return section.title;
  switch (section.kind) {
    case 'when_to_use':
      return 'Quand l’utiliser';
    case 'pitfall':
      return 'Piège';
    case 'example':
      return 'Exemple';
    default:
      return null;
  }
}

export function CardLessonView({
  title,
  sections,
  rule,
}: {
  title: string;
  sections: readonly CardSection[];
  /** Règle détaillée saisie par le prof (affichée si le recto est vide). */
  rule?: string;
}) {
  return (
    <article className="space-y-4">
      <h3 className="font-display text-ink-900 flex items-start gap-2 text-lg font-extrabold">
        <Lightbulb className="text-xp-500 mt-1 h-5 w-5 shrink-0" aria-hidden />
        <MathText>{title}</MathText>
      </h3>
      {sections.length === 0 && rule ? <MarkdownMath>{rule}</MarkdownMath> : null}
      {sections.map((section, i) => {
        const step = horaStepOf(section);
        const heading = sectionTitle(section);
        return (
          <section key={i} className="space-y-2">
            {(step || heading) && (
              <div className="flex flex-wrap items-center gap-2">
                {step && <HoraBadge step={step} />}
                {heading && <p className="text-ink-700 text-sm font-semibold">{heading}</p>}
              </div>
            )}
            <SectionBody section={section} />
          </section>
        );
      })}
    </article>
  );
}
